/**
 * Reschedule requests and where a booked round stands.
 *
 * The rules mirror the API's (see "Reschedule requests" in the backend's
 * docs/api.md) so the dialog can say what is wrong before anything is sent;
 * the API checks them again and has the last word.
 */

export const REASON_MIN = 10;
export const REASON_MAX = 500;
export const NOTE_MAX = 500;
export const REPLY_MIN = 4;
export const REPLY_MAX = 500;
export const MAX_SLOTS = 3;

export const REQUEST_STATUS_LABELS = {
  OPEN: 'Reschedule requested',
  ACCEPTED: 'Request accepted',
  DECLINED: 'Request declined',
  WITHDRAWN: 'Request withdrawn',
  EXPIRED: 'Request expired',
};

export const REQUEST_STATUS_TONES = {
  OPEN: 'warning',
  ACCEPTED: 'success',
  DECLINED: 'danger',
  WITHDRAWN: 'neutral',
  EXPIRED: 'neutral',
};

/** One empty row of the slot picker: a date and a start time, local. */
export function emptySlot() {
  return { date: '', time: '' };
}

/** The local date and time inputs as one instant, or null when incomplete. */
export function slotToDate(slot) {
  if (!slot?.date || !slot?.time) return null;
  const value = new Date(`${slot.date}T${slot.time}`);
  return Number.isNaN(value.getTime()) ? null : value;
}

/**
 * Checks the request dialog and builds the API payload.
 *
 * Returns `{ valid, errors, payload }`. `errors.slots` holds one message (or
 * null) per slot row, in order; `errors.slotsGeneral` is about the set.
 */
export function validateRescheduleRequest(
  { reason = '', slots = [], anyTime = false, note = '' },
  now = new Date(),
) {
  const errors = { reason: null, slots: slots.map(() => null), slotsGeneral: null, note: null };
  const trimmed = reason.trim();
  if (trimmed.length < REASON_MIN) {
    errors.reason = `Give a reason of at least ${REASON_MIN} characters.`;
  } else if (trimmed.length > REASON_MAX) {
    errors.reason = `Keep the reason to ${REASON_MAX} characters.`;
  }
  if (note.trim().length > NOTE_MAX) {
    errors.note = `Keep the note to ${NOTE_MAX} characters.`;
  }

  const instants = [];
  if (!anyTime) {
    // A row left completely empty is ignored; half a row is a mistake.
    const filled = slots.filter((slot) => slot.date || slot.time);
    if (filled.length === 0) {
      errors.slotsGeneral = 'Propose at least one time, or tick “Any time works”.';
    } else if (filled.length > MAX_SLOTS) {
      errors.slotsGeneral = `Propose at most ${MAX_SLOTS} times.`;
    }
    const seen = new Set();
    slots.forEach((slot, index) => {
      if (!slot.date && !slot.time) return;
      const value = slotToDate(slot);
      if (!value) {
        errors.slots[index] = 'Pick both a date and a start time.';
      } else if (value.getTime() <= now.getTime()) {
        errors.slots[index] = 'This time has already passed.';
      } else if (seen.has(value.getTime())) {
        errors.slots[index] = 'You have already proposed this time.';
      } else {
        seen.add(value.getTime());
        instants.push(value);
      }
    });
  }

  const valid =
    !errors.reason && !errors.note && !errors.slotsGeneral && errors.slots.every((e) => !e);
  return {
    valid,
    errors,
    payload: valid
      ? {
          reason: trimmed,
          proposed_slots: anyTime
            ? []
            : instants.sort((a, b) => a.getTime() - b.getTime()).map((d) => d.toISOString()),
          any_time: Boolean(anyTime),
          note: note.trim() || null,
        }
      : null,
  };
}

/** The company's reply when it declines: required, short. */
export function validateReply(reply = '') {
  const trimmed = reply.trim();
  if (trimmed.length < REPLY_MIN) return 'Write a short reply to the candidate.';
  if (trimmed.length > REPLY_MAX) return `Keep the reply to ${REPLY_MAX} characters.`;
  return null;
}

/** "Thu 16 Oct, 10:00" in the viewer's own locale and time zone. */
export function formatSlot(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time}`;
}

/** True when this round has a request waiting on the company. */
export function hasOpenRequest(interview) {
  return interview?.reschedule_request?.status === 'OPEN';
}

/**
 * Where a booked round stands, from the company's side. One answer for the
 * list, the next-step panel, the rounds list and the calendar, so they never
 * disagree.
 *
 * Keys, in the order they win:
 * - `requested`  the candidate asked for another time (warning);
 * - `declined`   the candidate can't make it (warning);
 * - `confirmed`  the candidate accepted (success);
 * - `awaiting`   sent and not answered yet (info);
 * - `closed`     not booked any more (completed, cancelled, no-show).
 *
 * `started` says the slot has begun. "Record the outcome" is offered only once
 * the candidate has accepted (`canRecordOutcome`); the API refuses it before.
 */
export function canRecordOutcome(interview) {
  return interview?.status === 'SCHEDULED' && interview.student_response === 'ACCEPTED';
}

export function interviewStanding(interview, now = new Date()) {
  if (!interview || interview.status !== 'SCHEDULED') {
    return { key: 'closed', label: null, tone: 'neutral', started: true };
  }
  const started = new Date(interview.scheduled_at).getTime() <= now.getTime();
  if (hasOpenRequest(interview)) {
    return {
      key: 'requested',
      label: 'Reschedule requested',
      tone: 'warning',
      started,
      detail: interview.reschedule_request.reason,
    };
  }
  if (interview.student_response === 'DECLINED') {
    return {
      key: 'declined',
      label: 'The candidate can’t make it',
      tone: 'warning',
      started,
      detail: interview.student_message || null,
    };
  }
  if (interview.student_response === 'ACCEPTED') {
    return { key: 'confirmed', label: 'Confirmed by the candidate', tone: 'success', started };
  }
  return {
    key: 'awaiting',
    label: 'Waiting for the candidate’s response',
    tone: 'info',
    started,
    sentAt: interview.created_at ?? null,
  };
}
